import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowRight,
  BadgeCheck,
  FileText,
  Rocket,
  ShieldCheck,
  Bike,
  GaugeCircle,
} from "lucide-react";
import { motion } from "framer-motion";
import {
  FaShieldAlt, // For Zero Depreciation
  FaTools, // For Battery Protection
  FaTruckPickup, // For Roadside Assistance
  FaOilCan, // For Consumables Cover
  FaFileInvoice, // For Return to Invoice
  FaCloudSunRain,
  FaUserLock,
  FaCarCrash,
  FaUserShield,
} from "react-icons/fa";
import { GiScooter } from "react-icons/gi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const AtherElectricScooterInsurance = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "Is it compulsory to insure my Ather scooter?",
      answer:
        "Yes, third-party insurance is legally required for all motorized vehicles in India.",
    },
    {
      question: "What type of insurance is best for Ather?",
      answer:
        "A comprehensive policy with battery and charger protection is highly recommended.",
    },
    {
      question: "How much does Ather scooter insurance cost?",
      answer:
        "Premiums start around ₹1,200 and may go up to ₹2,000 depending on model and add-ons.",
    },
    {
      question: "Is battery replacement covered under standard insurance?",
      answer: "No, unless you opt for a battery protection add-on.",
    },
    {
      question: "Can I claim insurance for charger theft?",
      answer:
        "Only if you’ve declared the charger and taken an accessories or charger protection add-on.",
    },
    {
      question: "Does Ather insurance include third-party liability?",
      answer:
        "Yes, all plans (even basic) include third-party coverage as mandated by law.",
    },
    {
      question: "How do I make a claim if my Ather gets damaged?",
      answer:
        "Inform your insurer, file a claim online or via app, visit an authorized garage, and submit required documents.",
    },
    {
      question: "Does Zero Depreciation help with EV parts?",
      answer:
        "Yes, it ensures you get full claim value without deductions for part wear-and-tear.",
    },
    {
      question: "What’s the role of IDV in EV insurance?",
      answer:
        "Insured Declared Value decides the maximum claim payout. Always choose an IDV close to the current market value.",
    },
    {
      question: "Are Ather scooters expensive to insure?",
      answer:
        "Not necessarily. Despite being premium, they cost less to insure than some petrol bikes due to lower risk of engine damage.",
    },
    {
      question: "Can I renew my Ather insurance online?",
      answer:
        "Yes, TheFinMaster allows paperless online renewals with instant policy issuance.",
    },
    {
      question: "Will my policy lapse if not renewed on time?",
      answer:
        "Yes, there’s usually a grace period, but late renewal can lead to inspections and loss of NCB.",
    },
    {
      question: "Do I need to declare modifications?",
      answer:
        "Yes, any performance or accessory modification should be declared to avoid claim issues.",
    },
    {
      question: "Can I get long-term insurance for my Ather scooter?",
      answer:
        "Yes, 3-year third-party coverage and 1–3-year own damage policies are available.",
    },
    {
      question: "What happens if I ride without insurance?",
      answer:
        "You can be fined up to ₹2,000 or more and may face legal penalties.",
    },
    {
      question: "Is Roadside Assistance available for EVs?",
      answer:
        "Yes, many insurers now provide EV-specific RSA, including towing, charging, and emergency support.",
    },
    {
      question: "Is Ather insurance available in all cities?",
      answer:
        "Yes, but availability of network garages and RSA may vary by city.",
    },
    {
      question: "Can I transfer my insurance if I sell my Ather?",
      answer:
        "Yes, the policy can be transferred along with ownership. NCB stays with the original owner.",
    },
    {
      question: "Is NCB applicable to electric scooters?",
      answer:
        "Yes, No Claim Bonus is valid for all motor vehicles, including electric scooters.",
    },
    {
      question: "What’s the benefit of Return to Invoice for Ather?",
      answer:
        "It ensures full invoice value is reimbursed in case of total loss or theft, including registration and road tax.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const cardsNew = [
    {
      title: "Battery Protection Cover",
      description:
        "Covers damage or replacement costs of the battery due to accidents.",
      icon: <FaTools className="text-4xl text-yellow-600 mb-4" />,
    },
    {
      title: "Zero Depreciation",
      description:
        "No depreciation deduction on plastic, rubber, or metal parts during claims.",
      icon: <FaShieldAlt className="text-4xl text-red-500 mb-4" />,
    },
    {
      title: "Roadside Assistance",
      description: "Vital in case of unexpected breakdowns or battery issues.",
      icon: <FaTruckPickup className="text-4xl text-blue-500 mb-4" />,
    },
    {
      title: "Consumables Cover",
      description:
        "Includes fluids, nuts, bolts, and other parts typically excluded.",
      icon: <FaOilCan className="text-4xl text-green-600 mb-4" />,
    },
    {
      title: "Charger & Wiring Cover",
      description:
        "Protects Ather's external charger and wiring setup from damage or theft.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
    {
      title: "Return to Invoice",
      description:
        "For new vehicles, helps recover full invoice amount in case of total loss or theft.",
      icon: <FaFileInvoice className="text-4xl text-purple-600 mb-4" />,
    },
  ];

  const features = [
    { icon: "🛡️", text: "Reliable & Accurate Estimates" },
    { icon: "⏱️", text: "Takes Less Than a Minute" },
    { icon: "📉", text: "Instantly Shows Premium Breakup" },
    { icon: "📄", text: "No Paperwork or Documentation" },
    { icon: "📲", text: "Easy to Use on Any Device" },
    { icon: "💰", text: "Helps You Save on Insurance" },
  ];

  const benefits = [
    {
      title: "Ather 450X",
      desc: "Premium electric scooter with top-tier performance.",
      icon: <Rocket className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Ather 450S",
      desc: "Affordable variant with essential features.",
      icon: <ShieldCheck className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "Ather 450 Apex",
      desc: "Limited-edition performance scooter.",
      icon: <Bike className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Ather Rizta",
      desc: "Family scooter with wider seats and higher utility.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
  ];
  const atherinsurance = [
    {
      name: "450X",
      path: "/ather-450x-bike-insurance",
      image: "/assets/ather450x.jpeg",
    },
    {
      name: "Rizta",
      path: "/ather-rizta-bike-insurance",
      image: "/assets/atherrizta.jpeg",
    },
    {
      name: "450S",
      path: "/ather-450s-bike-insurance",
      image: "/assets/ather450s.jpeg",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Ather Electric Scooter Insurance in India – Compare Quotes & Buy
          Online
        </title>
        <meta
          name="description"
          content="Explore Ather electric scooter insurance options in India. Compare quotes, customize coverage, and buy online to protect your Ather scooter."
        />
        <meta
          name="keywords"
          content="ather electric scooter insurance, ev scooter insurance india, ather 450x insurance, ather rizta insurance, two-wheeler insurance quotes"
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/ather-electrics-two-wheeler-insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/ather-electric-scooter-insurance" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Ather Electric Scooter Insurance in India" />
        <meta property="og:description" content="Compare and buy Ather electric scooter insurance online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/ather-electric-scooter-insurance" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">
              Ather Electric Scooter Insurance
            </span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">
                    Ather Electric Scooter Insurance{" "}
                  </span>{" "}
               
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                  Ather Energy has redefined urban commuting with its stylish,
                  high-performance electric scooters packed with smart features.
                  Whether you own the Ather 450X or the Ather 450S, safeguarding
                  your ride starts with the right insurance coverage. This guide
                  takes you through everything you need to know about insuring
                  an Ather electric scooter, from average premiums and model
                  insights to useful add-ons, renewal tips, and FAQs.
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/atherinsurance.jpeg"
                alt="Ather Electric Scooter"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            About Ather in India
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            Founded in 2013, Ather Energy is a Bengaluru-based startup that
            revolutionized the EV two-wheeler market in India. Its scooters are
            known for intelligent dashboards, fast acceleration, OTA updates,
            and battery reliability.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      <div className="p-4 sm:p-8 bg-white">
        <h2 className="text-xl sm:text-2xl font-bold mb-6 text-center">
          Choose Your Bike Brand
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {atherinsurance.map((calc, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.04 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <Link
                to={calc.path}
                className="flex flex-col items-center justify-center text-center border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all duration-300 h-full min-h-[160px]"
              >
                <img
                  src={calc.image}
                  alt={calc.name}
                  className="w-16 h-16 object-contain mb-2"
                />
                <span className="text-md font-medium text-gray-800 break-words leading-snug">
                  {calc.name}
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-blue-400 opacity-20 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-400 opacity-20 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-5xl mx-auto bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
            Why Ather Owners Need Insurance
          </h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            Electric vehicles, while low on maintenance, can be expensive to
            repair if damaged. With their smart components and lithium-ion
            batteries, Ather scooters benefit greatly from the financial
            protection insurance provides. Here’s why it's essential:
          </p>
          <ul className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 text-gray-700 mb-6">
            <li className="flex items-start gap-3">
              <FaCloudSunRain className="text-xl text-blue-500 mt-1" />
              <span>
                Mandatory under Indian law (at least third-party cover)
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserLock className="text-xl text-yellow-500 mt-1" />
              <span>Protects high-value components like battery and BMS</span>
            </li>
            <li className="flex items-start gap-3">
              <FaCarCrash className="text-xl text-red-500 mt-1" />
              <span>
                Covers risks like accidents, fire, theft, and natural calamities
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaUserShield className="text-xl text-indigo-500 mt-1" />
              <span>
                Enables cashless claims at authorized Ather service points
              </span>
            </li>
            <li className="flex items-start gap-3">
              <FaShieldAlt className="text-xl text-green-500 mt-1" />
              <span>Peace of mind for EV-first-time owners</span>
            </li>
          </ul>
          <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 rounded-lg text-indigo-700">
            A comprehensive policy ensures your Ather stays protected, legally
            compliant, and ready for the road.
          </div>
        </motion.div>
      </section>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your TwoWheeler Insurance <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your premium — no
              paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/bike-insurance-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>
                  {item.icon}
                </span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Popular Ather Models and Estimated Insurance Premiums
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Below are the estimated insured declared values (IDV) and starting
          insurance premiums for key Ather models:
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              {
                model: "Ather 450S",
                category: "Entry-Level EV",
                idv: "1.20 – 1.40 Lakh",
                premium: "₹1,200 – ₹1,500",
              },
              {
                model: "Ather 450X",
                category: "Premium EV",
                idv: "1.50 – 1.75 Lakh",
                premium: "₹1,400 – ₹1,800",
              },
              {
                model: "Ather 450 Apex",
                category: "Performance Variant",
                idv: "1.75 – 1.85 Lakh",
                premium: "₹1,600 – ₹2,000",
              },
              {
                model: "Ather Rizta",
                category: "Family Utility EV",
                idv: "1.30 – 1.45 Lakh",
                premium: "₹1,300 – ₹1,600",
              },
            ].map((item, index) => (
              <div
                key={index}
                className="border mb-4 p-4 rounded-lg bg-white shadow-md"
              >
                <h3 className="font-semibold text-gray-800 mb-2">
                  {item.model}
                </h3>
                <div className="text-gray-600 mb-1">
                  <strong>Category:</strong> {item.category}
                </div>
                <div className="text-gray-600 mb-1">
                  <strong>Approx. IDV:</strong> {item.idv}
                </div>
                <div className="text-gray-600">
                  <strong>Starting Premium:</strong> {item.premium}
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">
                    Ather Model
                  </th>
                  <th className="p-3 border border-gray-300 text-left">Type</th>
                  <th className="p-3 border border-gray-300 text-left">
                    Approx. IDV (₹)
                  </th>
                  <th className="p-3 border border-gray-300 text-left">
                    Starting Premium (₹)
                  </th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  [
                    "Ather 450S",
                    "Entry-Level EV",
                    "1.20 – 1.40 Lakh",
                    "₹1,200 – ₹1,500",
                  ],
                  [
                    "Ather 450X",
                    "Premium EV",
                    "1.50 – 1.75 Lakh",
                    "₹1,400 – ₹1,800",
                  ],
                  [
                    "Ather 450 Apex",
                    "Performance Variant",
                    "1.75 – 1.85 Lakh",
                    "₹1,600 – ₹2,000",
                  ],
                  [
                    "Ather Rizta",
                    "Family Utility EV",
                    "1.30 – 1.45 Lakh",
                    "₹1,300 – ₹1,600",
                  ],
                ].map(([model, category, idv, premium], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{category}</td>
                    <td className="p-3 border border-gray-300">{idv}</td>
                    <td className="p-3 border border-gray-300">{premium}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Recommended Add-Ons for Ather Insurance
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Electric vehicles have unique components and requirements. Here are
          some add-ons worth considering:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mt-4">
          {cardsNew.map((card, index) => (
            <div
              key={index}
              className="border rounded-lg shadow-lg p-6 flex flex-col items-center text-center"
            >
              {card.icon}
              <h3 className="text-lg font-semibold mb-2">{card.title}</h3>
              <p className="text-gray-600">{card.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <div className="mx-auto container p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Best Insurance Providers for Ather Scooters
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Ather scooters require insurers who understand EV-specific needs and
          offer prompt service:
        </p>
        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">⚡</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Digit Insurance
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              EV-friendly plans and app-based claims.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🚗</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Bajaj Allianz
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Wide network and flexible add-ons.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🏥</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              ICICI Lombard
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Covers EV-specific damages like charger or wiring.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              TATA AIG
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Good for high-value EVs with customizable IDV.
            </p>
          </div>
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📱</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">ACKO</h3>
            <p className="text-gray-700 text-sm mt-1">
              Known for quick digital claims and paperless process.
            </p>
          </div>
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Tips to Get the Best Ather Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              To get the best premium rates and coverage benefits, follow these
              smart tips:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Always compare premiums and features across insurers.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Choose a policy that includes battery coverage.</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Renew your policy before expiry to retain NCB benefits.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Declare all accessories and chargers during policy issuance.
                </span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>
                  Check if the insurer covers EV-specific parts and roadside
                  support.
                </span>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-emerald-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-6">
              <BadgeCheck className="w-8 h-8 text-emerald-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Needed for Ather Scooter Insurance
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              Have the following documents ready:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>RC (Registration Certificate)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Previous policy copy (if renewal)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Aadhar/PAN or other valid ID proof</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl">
                <span>Vehicle Invoice (especially for new vehicles)</span>
              </div>
              <div className="p-5 bg-emerald-50 dark:bg-gray-800 rounded-xl sm:col-span-2">
                <span>
                  PUC (Pollution Under Control) – for hybrid models if
                  applicable
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          FAQs About Ather Scooter Insurance
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <GiScooter />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Ather scooters are redefining urban mobility with electric
                power, smart design, and sustainable tech. But no matter how
                advanced your vehicle is, having the right insurance ensures
                you’re protected from the unexpected. Whether you’re commuting
                daily or exploring city roads, a tailored policy helps you stay
                covered.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                With TheFinMaster, finding and comparing insurance for your
                Ather scooter is quick and easy. Get quotes, explore add-ons,
                and ride with confidence.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default AtherElectricScooterInsurance;
